import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiError, UNKNOWN_ERROR_TRANSLATION_KEY } from '@data-access/api/error';
import { UISnackbarService } from '@ui/components';
import { REPLAY_REQUEST_AFTER_TOKEN_REFRESH } from '@util/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';

const REPLAYED_AFTER_REFRESH: HttpContextToken<boolean> = new HttpContextToken<boolean>(() => false);
const IDEMPOTENT_METHODS: string[] = ['GET', 'HEAD', 'OPTIONS'];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authService: AuthService = inject(AuthService);
  private readonly snackbar: UISnackbarService = inject(UISnackbarService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.authService.getToken$().pipe(
      catchError((error: unknown) => throwError(() => new ApiError(UNKNOWN_ERROR_TRANSLATION_KEY, error))),
      switchMap((token: string | null) => {
        const authorizedRequest: HttpRequest<unknown> = token
          ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
          : request;
        return next.handle(authorizedRequest).pipe(
          catchError((err: ApiError) => {
            const is401: boolean = err.originalError instanceof HttpErrorResponse && err.originalError.status == 401;
            if (!is401 || this.authService.isLoginPending()) {
              return throwError(() => err);
            }
            if (!request.context.get(REPLAYED_AFTER_REFRESH)) {
              return this.refreshAndReplay$(request, next, err);
            }
            this.logoutAfterUnauthorized(err);
            return throwError(() => err);
          }),
        );
      }),
    );
  }

  private refreshAndReplay$(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    unauthorizedError: ApiError,
  ): Observable<HttpEvent<unknown>> {
    return this.authService.refreshAfterUnauthorized$().pipe(
      catchError((refreshError: unknown) => {
        if (this.authService.isSessionRejected(refreshError)) {
          this.logoutAfterUnauthorized(unauthorizedError);
        }
        return throwError(() => unauthorizedError);
      }),
      switchMap(() =>
        this.isReplayable(request)
          ? this.intercept(this.markAsReplayed(request), next)
          : throwError(() => unauthorizedError),
      ),
    );
  }

  private isReplayable(request: HttpRequest<unknown>): boolean {
    return IDEMPOTENT_METHODS.includes(request.method) || request.context.get(REPLAY_REQUEST_AFTER_TOKEN_REFRESH);
  }

  private markAsReplayed(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const context: HttpContext = new HttpContext();
    for (const token of request.context.keys()) {
      context.set(token, request.context.get(token));
    }
    return request.clone({ context: context.set(REPLAYED_AFTER_REFRESH, true) });
  }

  private logoutAfterUnauthorized(err: ApiError): void {
    const isLockedOut: boolean = err.originalError?.error?.detail === 'error.user.lockedOut';
    if (!isLockedOut && this.authService.isLoggedIn()) {
      this.snackbar.showNeutral('Your session has expired, please log in again');
    }
    this.authService.logout();
  }
}
